import { useCallback, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useFilters } from "../hooks/ControleMateriais_lista/filters";
import { useComtroleMateriaisListaPagination } from "../hooks/ControleMateriais_lista/paginagion";
import { GetAllControleMateriaisDao } from "../../data/daos/read/get_all_controle_materiais";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { ControleMateriaisForm } from "../schemas/ControleMateriais_lista/ControleMateriais";
import { CreateControleMateriaisRequest } from "../../data/daos/create/create_controle_materiais";
import { set } from "zod";
import { downloadBase64File } from "../../../../common/utils/download_utils";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";

type LoadFilterFuncState = {
  nroMaterial?: string;
  descricao?: string;
  ativo?: string;
  CurrentPage?: number | null;
  RowsPerPage?: number | null;
};

export const useControleMateriaisListaViewModel = () => {
  const {
    reports: {
      getReportMateriaisControladosUseCase,
    },
    obras: {
      getAllControleMateriaisUseCase,
      createControleMateriaisUseCase,
      updateControleMateriaisUseCase,
      getControleMateriaisByIdUseCase,
      deleteControleMateriaisUseCase,
      downloadMateriaisControladosByIdUseCase,
    },
  } = useDependencyInjection();
  const filters = useFilters();
  const pagination = useComtroleMateriaisListaPagination();
  const [controleMateriais, setControleMateriais] = useState<GetAllControleMateriaisDao[]>([]);
  const [controleMateriaiscreate, setControleMateriaiscreate] = useState<CreateControleMateriaisRequest>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [loading, setLoading] = useState(false);
  const { pushErrorNotification } = usePageLoader();
  const debouncedLoad = useCreateDebounceHandler(350);
  const [abrirModal, setAbrirModal] = useState(false);
  const [editmod, seteditmod] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dadosEdit, setDadosEdit] = useState<ControleMateriaisForm | null>(null);
  const [IdMateriaisControlados, setIdMateriaisControlados] = useState<number>();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  //#region Loads

  usePageStartBootstrap(async () => {
    try {
      await load();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados das paginas!")[0]);
    }
  });

  //#endregion

  //#region Getall / Tabela
  const load = useCallback(
    async (funcState?: LoadFilterFuncState) => {
      try {
        const filterpage = funcState?.CurrentPage ?? pagination.page;
        const filterRowspage = funcState?.RowsPerPage ?? pagination.rowsPerPage;
        const filternroMaterial = funcState?.nroMaterial ?? filters.numero ?? "";
        const filterdescricao = funcState?.descricao ?? filters.descricao ?? "";
        const filterativo = funcState?.ativo ?? filters.status ?? "T";

        await getAllControleMateriaisUseCase.getAllControleMateriais(
          filternroMaterial,
          filterdescricao,
          filterativo,
          filterpage,
          filterRowspage,
        );
        setControleMateriais(getAllControleMateriaisUseCase.items);
        pagination.setRowsPerPage(getAllControleMateriaisUseCase.pageSize);
        pagination.setTotalItems(getAllControleMateriaisUseCase.totalItems);
      } catch (e: unknown) {
        throw new Error("Falha ao buscar motivos de afastamento!");
      }
    },
    [getAllControleMateriaisUseCase, pagination, filters],
  );

  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useComtroleMateriaisListaPagination.setState({ page: page + 1 });
        //await load({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    },
    [pushErrorNotification, load],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useComtroleMateriaisListaPagination.setState({
          page: 1,
          rowsPerPage,
        });
        await load({ CurrentPage: 1, RowsPerPage: rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    },
    [pushErrorNotification, load],
  );
  //#endregion

  //#region Filtro

  const handleDescricaoChange = useDebouncedCallback(
    debouncedLoad,
    (descricao: string | null) => {
      filters.setDescricao(descricao);
      return async () => {
        try {
          await load({ descricao: descricao ?? undefined });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
        }
      };
    },
    [filters, load],
  );

  const handleNumeroChange = useDebouncedCallback(
    debouncedLoad,
    (numero: string | null) => {
      filters.setNumero(numero);
      return async () => {
        try {
          await load({ nroMaterial: numero ?? undefined });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
        }
      };
    },
    [filters, load],
  );

  const handleStatusChange = useCallback(
    (novoStatus: string | null) => {
      filters.setStatus(novoStatus ?? "A");
      load({ ativo: novoStatus ?? "A" });
    },
    [filters, load],
  );

  const handleClear = useCallback(async () => {
    filters.clear();
    try {
      await load({
        ativo: "T",
        descricao: undefined,
        nroMaterial: undefined,
        CurrentPage: undefined,
        RowsPerPage: undefined,
      });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao atualizar pagina!"));
    }
  }, [filters, load]);
  //#endregion

  function converteArquivoParaBase64(file?: File | null): Promise<string | undefined> {
    if (!file) {
      return Promise.resolve(undefined);
    }
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          const base64Pure = reader.result.split(",")[1];

          resolve(base64Pure);
        } else {
          reject(new Error("Erro ao ler o arquivo: resultado não é string."));
        }
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  }

  const handleDownload = useCallback(async (idMateriaisControlados: number, nomeArquivo: string) => {
    try {
      setLoading(true);
      await downloadMateriaisControladosByIdUseCase
        .downloadMateriaisControladosById(idMateriaisControlados, nomeArquivo);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao fazer download do arquivo!"));
    } finally {
      setLoading(false);
    }
  }, [
    downloadMateriaisControladosByIdUseCase,
    pushErrorNotification,
  ]);

  //#region Create/Update

  const abrirModalCreate = useCallback(() => {
    setDadosEdit(null);
    setAbrirModal(true);
    seteditmod(false);
  }, [setAbrirModal, seteditmod, abrirModal, dadosEdit, editmod]);

  const fecharmodal = useCallback(() => {
    setAbrirModal(false);
    setDadosEdit(null);
    seteditmod(false);
  }, [setDadosEdit, dadosEdit, editmod]);

  const handleDocumentoSubmit = useCallback(
    async (form: ControleMateriaisForm) => {
      try {
        setSaving(true);
        if (editmod == false) {
          await createControleMateriaisUseCase.createControleMateriais({
            nroMaterial: form.nroMaterial,
            descricao: form.descricao,
            ativo: form.ativo,
            nomeArquivo: form.nomeArquivo,
            arquivo: await converteArquivoParaBase64(form.arquivo),
          });
          await load();
        } else {
          await updateControleMateriaisUseCase.updateControleMateriais({
            idMateriaisControlados: form.idMateriaisControlados,
            nroMaterial: form.nroMaterial,
            descricao: form.descricao,
            ativo: form.ativo,
            nomeArquivo: form.nomeArquivo,
            arquivo: await converteArquivoParaBase64(form.arquivo),
          });
          await load();
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar documentos!"));
      } finally {
        setSaving(false);
        setAbrirModal(false);
      }
    },
    [createControleMateriaisUseCase, updateControleMateriaisUseCase, editmod],
  );
  //#endregion

  //#region GetById

  function base64ToFile(base64: string, filename: string): File {
    const arr = base64.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
    const bstr = atob(arr.length > 1 ? arr[1] : arr[0]);
    const n = bstr.length;
    const u8arr = new Uint8Array(n);

    for (let i = 0; i < n; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }

    return new File([u8arr], filename, { type: mime });
  }

  const getbyid = useCallback(
    async (id: number | null) => {
      setLoading(true);
      try {
        if (id != null) {
          const response = await getControleMateriaisByIdUseCase.getControleMateriaisById(id);

          const file =
            response.arquivo && typeof response.arquivo === "string"
              ? base64ToFile(response.arquivo, response.nomeArquivo || "arquivo")
              : null;

          await setDadosEdit({
            ...response,
            arquivo: file, // agora o form tem o File
          } as ControleMateriaisForm);
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar documento!"));
      } finally {
        setLoading(false);
      }
    },
    [getControleMateriaisByIdUseCase, setDadosEdit],
  );

  const handleEditControleMateriaisOpenState = useCallback(
    (idMateriaisControlados: number | null, openState: boolean) => {
      if (openState) {
        getbyid(idMateriaisControlados);
        setAbrirModal(true);
        seteditmod(true);
      }
    },
    [getbyid, editmod],
  );

  //#endregion

  //#region Delete

  const DeleteOpenModal = useCallback(
    (id: number | null, openState: boolean) => {
      if (openState) {
        if (id != null) {
          setIdMateriaisControlados(id);
        } else {
          setIdMateriaisControlados(undefined);
        }
      }
      setOpenDeleteModal(openState);
    },
    [IdMateriaisControlados, setIdMateriaisControlados],
  );

  const DeletarMateriaisControlados = useCallback(async () => {
    setLoading(true);
    if (IdMateriaisControlados === undefined) return;
    try {
      await deleteControleMateriaisUseCase.deleteControleMateriais(IdMateriaisControlados);
      await load();
    } catch (e: unknown) {
      const raw: string | string[] = processError(e, "Falha ao buscar dados da página!");
      const msg = Array.isArray(raw) ? raw.join("\u000A") : (raw as string).replace(/\n/g, "\u000A");

      pushErrorNotification("Erro", msg);
    } finally {
      setLoading(false);
      setOpenDeleteModal(false);
    }
  }, [IdMateriaisControlados, deleteControleMateriaisUseCase]);

  const handleGerarRelatorio = useCallback(async () => {
    try {
      setLoading(true);

      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportMateriaisControladosUseCase.getReportMateriaisControlados(requiredMimeType),
      });

    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao gerar relatório!"));
    } finally {
      setLoading(false);
    }
  }, [
    getReportMateriaisControladosUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  //#endregion
  return {
    filters,
    pagination,
    loading,
    controleMateriais: controleMateriais ?? [],
    abrirModal,
    saving,
    dadosEdit,
    openDeleteModal,
    editmod,
    reportViewerState,
    handlePageChange,
    handleRowsPerPageChange,
    handleDescricaoChange,
    handleNumeroChange,
    handleStatusChange,
    handleClear,
    fecharmodal,
    abrirModalCreate,
    handleDocumentoSubmit,
    handleEditControleMateriaisOpenState,
    DeleteOpenModal,
    DeletarMateriaisControlados,
    handleDownload,
    handleGerarRelatorio,
  };
};
